/* eslint-disable */
/*
 * Vendored Lucide icons.
 *
 * lucide-react's published builds are incompatible with this project's
 * toolchain (react-scripts 4 / webpack 4): the ESM build does named React
 * imports inside .mjs files (which webpack 4 rejects), and the CJS build ships
 * ES2020 syntax that react-scripts won't transpile in node_modules. To use
 * genuine Lucide icons we vendor the icon path data here and render it through
 * a tiny local factory that compiles like any other app source.
 *
 * Icon path data is copied verbatim from lucide-react (ISC licensed). Regenerate
 * with scripts that read node_modules/lucide-react/dist/esm/icons/.
 * Import from here: import { Video, Bell } from 'Utils/lucideIcons';
 */
import React, { forwardRef, createElement } from 'react';

const DEFAULTS = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const createLucideIcon = (iconName, iconNode) => {
  const Component = forwardRef(
    ({ color = 'currentColor', size = 24, strokeWidth = 2, absoluteStrokeWidth, ...rest }, ref) => createElement(
      'svg',
      {
        ref,
        ...DEFAULTS,
        width: size,
        height: size,
        stroke: color,
        strokeWidth: absoluteStrokeWidth ? (Number(strokeWidth) * 24) / Number(size) : strokeWidth,
        className: `lucide lucide-${iconName.toLowerCase()}`,
        ...rest,
      },
      iconNode.map(([tag, attrs]) => createElement(tag, { key: attrs.key, ...attrs })),
    ),
  );
  Component.displayName = iconName;
  return Component;
};

export const Video = /* @__PURE__ */ createLucideIcon('Video', [
  ["path", { d: "m22 8-6 4 6 4V8Z", key: "50v9me" }],
  [
    "rect",
    {
      width: "14",
      height: "12",
      x: "2",
      y: "6",
      rx: "2",
      ry: "2",
      key: "1rqjg6"
    }
  ]
]);

export const Image = /* @__PURE__ */ createLucideIcon('Image', [
  [
    "rect",
    {
      width: "18",
      height: "18",
      x: "3",
      y: "3",
      rx: "2",
      ry: "2",
      key: "1m3agn"
    }
  ],
  ["circle", { cx: "9", cy: "9", r: "2", key: "af1f0g" }],
  ["path", { d: "m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21", key: "1xmnt7" }]
]);

export const CloudUpload = /* @__PURE__ */ createLucideIcon('CloudUpload', [
  [
    "path",
    {
      d: "M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242",
      key: "1pljnt"
    }
  ],
  ["path", { d: "M12 12v9", key: "192myk" }],
  ["path", { d: "m16 16-4-4-4 4", key: "119tzi" }]
]);

export const Upload = /* @__PURE__ */ createLucideIcon('Upload', [
  ["path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4", key: "ih7n3h" }],
  ["polyline", { points: "17 8 12 3 7 8", key: "t8dd8p" }],
  ["line", { x1: "12", x2: "12", y1: "3", y2: "15", key: "widbto" }]
]);

export const Users = /* @__PURE__ */ createLucideIcon('Users', [
  ["path", { d: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2", key: "1yyitq" }],
  ["circle", { cx: "9", cy: "7", r: "4", key: "nufk8" }],
  ["path", { d: "M22 21v-2a4 4 0 0 0-3-3.87", key: "kshegd" }],
  ["path", { d: "M16 3.13a4 4 0 0 1 0 7.75", key: "1da9ce" }]
]);

export const Clock = /* @__PURE__ */ createLucideIcon('Clock', [
  ["circle", { cx: "12", cy: "12", r: "10", key: "1mglay" }],
  ["polyline", { points: "12 6 12 12 16 14", key: "68esgv" }]
]);

export const HardDrive = /* @__PURE__ */ createLucideIcon('HardDrive', [
  ["line", { x1: "22", x2: "2", y1: "12", y2: "12", key: "1y58io" }],
  [
    "path",
    {
      d: "M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z",
      key: "oot6mr"
    }
  ],
  ["line", { x1: "6", x2: "6.01", y1: "16", y2: "16", key: "sgf278" }],
  ["line", { x1: "10", x2: "10.01", y1: "16", y2: "16", key: "1l4acy" }]
]);

export const Plus = /* @__PURE__ */ createLucideIcon('Plus', [
  ["path", { d: "M5 12h14", key: "1ays0h" }],
  ["path", { d: "M12 5v14", key: "s699le" }]
]);

export const Settings = /* @__PURE__ */ createLucideIcon('Settings', [
  [
    "path",
    {
      d: "M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z",
      key: "1qme2f"
    }
  ],
  ["circle", { cx: "12", cy: "12", r: "3", key: "1v7zrd" }]
]);

export const Sparkles = /* @__PURE__ */ createLucideIcon('Sparkles', [
  [
    "path",
    {
      d: "m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z",
      key: "17u4zn"
    }
  ],
  ["path", { d: "M5 3v4", key: "bklmnn" }],
  ["path", { d: "M19 17v4", key: "iiml17" }],
  ["path", { d: "M3 5h4", key: "nem4j1" }],
  ["path", { d: "M17 19h4", key: "lbex7p" }]
]);

export const Smartphone = /* @__PURE__ */ createLucideIcon('Smartphone', [
  [
    "rect",
    {
      width: "14",
      height: "20",
      x: "5",
      y: "2",
      rx: "2",
      ry: "2",
      key: "1yt0o3"
    }
  ],
  ["path", { d: "M12 18h.01", key: "mhygvu" }]
]);

export const BookOpen = /* @__PURE__ */ createLucideIcon('BookOpen', [
  ["path", { d: "M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z", key: "vv98re" }],
  ["path", { d: "M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z", key: "1cyq3y" }]
]);

export const Crown = /* @__PURE__ */ createLucideIcon('Crown', [
  ["path", { d: "m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14", key: "zkxr6b" }]
]);

export const ArrowUpRight = /* @__PURE__ */ createLucideIcon('ArrowUpRight', [
  ["path", { d: "M7 7h10v10", key: "1tivn9" }],
  ["path", { d: "M7 17 17 7", key: "1vkiza" }]
]);

export const ArrowDown = /* @__PURE__ */ createLucideIcon('ArrowDown', [
  ["path", { d: "M12 5v14", key: "s699le" }],
  ["path", { d: "m19 12-7 7-7-7", key: "1idqje" }]
]);

export const ArrowUp = /* @__PURE__ */ createLucideIcon('ArrowUp', [
  ["path", { d: "m5 12 7-7 7 7", key: "hav0vg" }],
  ["path", { d: "M12 19V5", key: "x0mq9r" }]
]);

export const Bell = /* @__PURE__ */ createLucideIcon('Bell', [
  ["path", { d: "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9", key: "1qo2s2" }],
  ["path", { d: "M10.3 21a1.94 1.94 0 0 0 3.4 0", key: "qgo35s" }]
]);

export const LayoutDashboard = /* @__PURE__ */ createLucideIcon('LayoutDashboard', [
  ["rect", { width: "7", height: "9", x: "3", y: "3", rx: "1", key: "10lvy0" }],
  [
    "rect",
    { width: "7", height: "5", x: "14", y: "3", rx: "1", key: "16une8" }
  ],
  [
    "rect",
    { width: "7", height: "9", x: "14", y: "12", rx: "1", key: "1hutg5" }
  ],
  [
    "rect",
    { width: "7", height: "5", x: "3", y: "16", rx: "1", key: "ldoo1y" }
  ]
]);

export const LayoutGrid = /* @__PURE__ */ createLucideIcon('LayoutGrid', [
  ["rect", { width: "7", height: "7", x: "3", y: "3", rx: "1", key: "1g98yp" }],
  [
    "rect",
    { width: "7", height: "7", x: "14", y: "3", rx: "1", key: "6d4xhi" }
  ],
  [
    "rect",
    { width: "7", height: "7", x: "14", y: "14", rx: "1", key: "nxv5o0" }
  ],
  [
    "rect",
    { width: "7", height: "7", x: "3", y: "14", rx: "1", key: "1bb6yr" }
  ]
]);

export const Film = /* @__PURE__ */ createLucideIcon('Film', [
  [
    "rect",
    {
      width: "20",
      height: "20",
      x: "2",
      y: "2",
      rx: "2.18",
      ry: "2.18",
      key: "vury4c"
    }
  ],
  ["line", { x1: "7", x2: "7", y1: "2", y2: "22", key: "t1e4qh" }],
  ["line", { x1: "17", x2: "17", y1: "2", y2: "22", key: "1tliql" }],
  ["line", { x1: "2", x2: "22", y1: "12", y2: "12", key: "1dnqot" }],
  ["line", { x1: "2", x2: "7", y1: "7", y2: "7", key: "1wdzzh" }],
  ["line", { x1: "2", x2: "7", y1: "17", y2: "17", key: "2fufxq" }],
  ["line", { x1: "17", x2: "22", y1: "17", y2: "17", key: "1xg577" }],
  ["line", { x1: "17", x2: "22", y1: "7", y2: "7", key: "acrv2l" }]
]);

export const MonitorPlay = /* @__PURE__ */ createLucideIcon('MonitorPlay', [
  ["path", { d: "m10 7 5 3-5 3Z", key: "29ljg6" }],
  [
    "rect",
    { width: "20", height: "14", x: "2", y: "3", rx: "2", key: "48i651" }
  ],
  ["path", { d: "M12 17v4", key: "1riwvh" }],
  ["path", { d: "M8 21h8", key: "1ev6f3" }]
]);

export const Projector = /* @__PURE__ */ createLucideIcon('Projector', [
  ["path", { d: "M5 7 3 5", key: "pj-a" }],
  ["path", { d: "M9 6V3", key: "pj-b" }],
  ["path", { d: "m13 7 2-2", key: "pj-c" }],
  ["circle", { cx: "9", cy: "13", r: "3", key: "pj-d" }],
  ["path", { d: "M11.83 12H20a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h2.17", key: "pj-e" }],
  ["path", { d: "M16 16h2", key: "pj-f" }]
]);

export const User = /* @__PURE__ */ createLucideIcon('User', [
  ["path", { d: "M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2", key: "975kel" }],
  ["circle", { cx: "12", cy: "7", r: "4", key: "17ys0d" }]
]);

export const ChevronRight = /* @__PURE__ */ createLucideIcon('ChevronRight', [
  ["path", { d: "m9 18 6-6-6-6", key: "mthhwq" }]
]);

export const Disc3 = /* @__PURE__ */ createLucideIcon('Disc3', [
  ["circle", { cx: "12", cy: "12", r: "10", key: "1mglay" }],
  ["path", { d: "M6 12c0-1.7.7-3.2 1.8-4.2", key: "oqkarx" }],
  ["circle", { cx: "12", cy: "12", r: "2", key: "1c9p78" }],
  ["path", { d: "M18 12c0 1.7-.7 3.2-1.8 4.2", key: "1eah9h" }]
]);

export const Circle = /* @__PURE__ */ createLucideIcon('Circle', [
  ["circle", { cx: "12", cy: "12", r: "10", key: "1mglay" }]
]);

export const UserPlus = /* @__PURE__ */ createLucideIcon('UserPlus', [
  ["path", { d: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2", key: "1yyitq" }],
  ["circle", { cx: "9", cy: "7", r: "4", key: "nufk8" }],
  ["line", { x1: "19", x2: "19", y1: "8", y2: "14", key: "1bvyxn" }],
  ["line", { x1: "22", x2: "16", y1: "11", y2: "11", key: "1shjgl" }]
]);

export const UserRoundPlus = /* @__PURE__ */ createLucideIcon('UserRoundPlus', [
  ["path", { d: "M2 21a8 8 0 0 1 13.292-6", key: "urp1" }],
  ["circle", { cx: "10", cy: "8", r: "5", key: "urp2" }],
  ["path", { d: "M19 16v6", key: "urp3" }],
  ["path", { d: "M22 19h-6", key: "urp4" }]
]);

export const Check = /* @__PURE__ */ createLucideIcon('Check', [
  ["path", { d: "M20 6 9 17l-5-5", key: "1gmf2c" }]
]);

export const Copy = /* @__PURE__ */ createLucideIcon('Copy', [
  [
    "rect",
    {
      width: "14",
      height: "14",
      x: "8",
      y: "8",
      rx: "2",
      ry: "2",
      key: "17jyea"
    }
  ],
  [
    "path",
    {
      d: "M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2",
      key: "zix9uf"
    }
  ]
]);

export const Link2 = /* @__PURE__ */ createLucideIcon('Link2', [
  ["path", { d: "M9 17H7A5 5 0 0 1 7 7h2", key: "8i5ue5" }],
  ["path", { d: "M15 7h2a5 5 0 1 1 0 10h-2", key: "1b9ql8" }],
  ["line", { x1: "8", x2: "16", y1: "12", y2: "12", key: "1jonct" }]
]);

export const Send = /* @__PURE__ */ createLucideIcon('Send', [
  ["path", { d: "m22 2-7 20-4-9-9-4Z", key: "1q3vgg" }],
  ["path", { d: "M22 2 11 13", key: "nzbqef" }]
]);

export const ExternalLink = /* @__PURE__ */ createLucideIcon('ExternalLink', [
  ["path", { d: "M15 3h6v6", key: "1q9fwt" }],
  ["path", { d: "M10 14 21 3", key: "gplh6r" }],
  ["path", { d: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6", key: "a6xqqp" }]
]);

export const Search = /* @__PURE__ */ createLucideIcon('Search', [
  ["circle", { cx: "11", cy: "11", r: "8", key: "4ej97u" }],
  ["path", { d: "m21 21-4.3-4.3", key: "1qie3q" }]
]);

export const Filter = /* @__PURE__ */ createLucideIcon('Filter', [
  [
    "polygon",
    { points: "22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3", key: "1yg77f" }
  ]
]);

export const X = /* @__PURE__ */ createLucideIcon('X', [
  ["path", { d: "M18 6 6 18", key: "1bl5f8" }],
  ["path", { d: "m6 6 12 12", key: "d8bk6v" }]
]);

export const Trash2 = /* @__PURE__ */ createLucideIcon('Trash2', [
  ["path", { d: "M3 6h18", key: "d0wm0j" }],
  ["path", { d: "M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6", key: "4alrt4" }],
  ["path", { d: "M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2", key: "v07s0e" }],
  ["line", { x1: "10", x2: "10", y1: "11", y2: "17", key: "1uufr5" }],
  ["line", { x1: "14", x2: "14", y1: "11", y2: "17", key: "xtxkd" }]
]);

export const Layers = /* @__PURE__ */ createLucideIcon('Layers', [
  ["polygon", { points: "12 2 2 7 12 12 22 7 12 2", key: "1b0ttc" }],
  ["polyline", { points: "2 17 12 22 22 17", key: "imjtdl" }],
  ["polyline", { points: "2 12 12 17 22 12", key: "5dexcv" }]
]);

export const Pencil = /* @__PURE__ */ createLucideIcon('Pencil', [
  ["path", { d: "M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z", key: "1a8usu" }],
  ["path", { d: "m15 5 4 4", key: "1mk7zo" }]
]);

export const SquarePen = /* @__PURE__ */ createLucideIcon('SquarePen', [
  ["path", { d: "M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7", key: "1cz9lv" }],
  ["path", { d: "M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z", key: "wa1lgi" }]
]);

export const Loader2 = /* @__PURE__ */ createLucideIcon('Loader2', [
  ["path", { d: "M21 12a9 9 0 1 1-6.219-8.56", key: "13oakl" }]
]);

export const Info = /* @__PURE__ */ createLucideIcon('Info', [
  ["circle", { cx: "12", cy: "12", r: "10", key: "1mglay" }],
  ["path", { d: "M12 16v-4", key: "1dtifu" }],
  ["path", { d: "M12 8h.01", key: "e9boi3" }]
]);

export const CircleCheck = /* @__PURE__ */ createLucideIcon('CircleCheck', [
  ["circle", { cx: "12", cy: "12", r: "10", key: "1mglay" }],
  ["path", { d: "m9 12 2 2 4-4", key: "1gnqz4" }]
]);

export const Ban = /* @__PURE__ */ createLucideIcon('Ban', [
  ["circle", { cx: "12", cy: "12", r: "10", key: "1mglay" }],
  ["path", { d: "m4.9 4.9 14.2 14.2", key: "1m5liu" }]
]);

export const RefreshCw = /* @__PURE__ */ createLucideIcon('RefreshCw', [
  ["path", { d: "M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8", key: "1357e3" }],
  ["path", { d: "M3 3v5h5", key: "1xhq8a" }],
  ["path", { d: "M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16", key: "1uedjt" }],
  ["path", { d: "M16 16h5v5", key: "ccwih5" }]
]);

export const LogOut = /* @__PURE__ */ createLucideIcon('LogOut', [
  ["path", { d: "m16 17 5-5-5-5", key: "1bji2h" }],
  ["path", { d: "M21 12H9", key: "dn1m92" }],
  ["path", { d: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4", key: "1uf3rs" }]
]);

export const CreditCard = /* @__PURE__ */ createLucideIcon('CreditCard', [
  ["rect", { width: "20", height: "14", x: "2", y: "5", rx: "2", key: "ynyp8z" }],
  ["line", { x1: "2", x2: "22", y1: "10", y2: "10", key: "1b3vmo" }]
]);

export const Mic = /* @__PURE__ */ createLucideIcon('Mic', [
  ["path", { d: "M12 19v3", key: "npa21l" }],
  ["path", { d: "M19 10v2a7 7 0 0 1-14 0v-2", key: "1vc78b" }],
  ["rect", { x: "9", y: "2", width: "6", height: "13", rx: "3", key: "s6n7sd" }]
]);

export const ArrowUpDown = /* @__PURE__ */ createLucideIcon('ArrowUpDown', [
  ["path", { d: "m21 16-4 4-4-4", key: "f6ql7i" }],
  ["path", { d: "M17 20V4", key: "1ejh1v" }],
  ["path", { d: "m3 8 4-4 4 4", key: "11wl7u" }],
  ["path", { d: "M7 4v16", key: "1glfcx" }]
]);

export const ChevronUp = /* @__PURE__ */ createLucideIcon('ChevronUp', [
  ["path", { d: "m18 15-6-6-6 6", key: "153udz" }]
]);

export const ChevronDown = /* @__PURE__ */ createLucideIcon('ChevronDown', [
  ["path", { d: "m6 9 6 6 6-6", key: "qrunsl" }]
]);
